import React, { useState } from 'react';
import { Logo } from '../components/Logo';
import {
  Lock,
  Mail,
  Eye,
  EyeOff,
  ArrowRight,
  ShieldCheck,
  ArrowLeft,
  AlertCircle,
  CheckCircle2,
  Boxes,
  KeyRound,
  Building2
} from 'lucide-react';

interface AdminLoginPageProps {
  onNavigate: (page: string) => void;
  onLoginSuccess?: () => void;
}

export const ADMIN_AUTH_KEY = 'saints_tech_admin_session';

export function checkIsAdminAuthenticated(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const session = localStorage.getItem(ADMIN_AUTH_KEY);
    if (!session) return false;
    const parsed = JSON.parse(session);
    return Boolean(parsed && parsed.authenticated);
  } catch {
    return false;
  }
}

export function performAdminLogout(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(ADMIN_AUTH_KEY);
  } catch {
    // ignore
  }
}

export const AdminLoginPage: React.FC<AdminLoginPageProps> = ({ onNavigate, onLoginSuccess }) => {
  const [email, setEmail] = useState('admin@saintstech.co.ke');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();

    if (!cleanEmail) {
      setErrorMessage('Please enter your administrator work email address.');
      return;
    }

    if (!cleanPassword) {
      setErrorMessage('Please enter your admin console security password.');
      return;
    }

    setIsSubmitting(true);

    // Simulate verification with quick delay
    setTimeout(() => {
      // Allow standard admin passwords: 'admin', 'admin123', 'saintstech', 'saintstech2026', or any 4+ char password
      const isValid =
        cleanPassword === 'admin' ||
        cleanPassword === 'admin123' ||
        cleanPassword === 'saintstech' ||
        cleanPassword === 'saintstech2026' ||
        cleanPassword.length >= 4;

      if (isValid) {
        setLoginSuccess(true);
        const sessionData = {
          email: cleanEmail,
          authenticated: true,
          role: 'SUPER_ADMIN',
          fullName: 'Saints Tech Operations Admin',
          timestamp: Date.now()
        };
        try {
          localStorage.setItem(ADMIN_AUTH_KEY, JSON.stringify(sessionData));
        } catch {
          // ignore
        }

        setTimeout(() => {
          if (onLoginSuccess) {
            onLoginSuccess();
          } else {
            onNavigate('admin');
          }
        }, 600);
      } else {
        setErrorMessage('Invalid password credentials. Please verify your administrator credentials.');
        setIsSubmitting(false);
      }
    }, 450);
  };

  const handleFillDemoCredentials = () => {
    setEmail('admin@saintstech.co.ke');
    setPassword('admin123');
    setErrorMessage(null);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between selection:bg-sky-500 selection:text-white">
      {/* Top Header */}
      <header className="px-6 py-5 border-b border-slate-900 bg-slate-950/80 backdrop-blur-md flex items-center justify-between">
        <button
          type="button"
          onClick={() => onNavigate('home')}
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-white transition-colors cursor-pointer group"
        >
          <ArrowLeft className="w-4 h-4 text-sky-400 group-hover:-translate-x-0.5 transition-transform" />
          <span>Return to Storefront</span>
        </button>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          <span>256-bit Encrypted Portal</span>
        </div>
      </header>

      {/* Main Authentication Card */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md space-y-6">
          {/* Brand header */}
          <div className="text-center space-y-2">
            <div className="inline-flex p-3 rounded-2xl bg-gradient-to-br from-sky-500/10 to-blue-600/20 border border-sky-500/20 text-sky-400 mb-1">
              <Boxes className="w-8 h-8 text-sky-400" />
            </div>
            <h1 className="text-2xl font-extrabold text-white tracking-tight">
              Saints Tech Solutions
            </h1>
            <p className="text-xs text-sky-400 uppercase tracking-widest font-semibold">
              Admin & Operations Console
            </p>
            <p className="text-xs text-slate-400 max-w-xs mx-auto">
              Secure authentication for machinery catalog, quote RFQ leads, and technical articles management.
            </p>
          </div>

          {/* Login Form Box */}
          <div className="bg-slate-900/90 rounded-2xl border border-slate-800 p-6 sm:p-8 shadow-2xl space-y-5">
            {errorMessage && (
              <div className="p-3.5 rounded-xl bg-rose-950/40 border border-rose-800/80 text-xs text-rose-300 flex items-start gap-2.5 animate-fadeIn">
                <AlertCircle className="w-4 h-4 shrink-0 text-rose-400 mt-0.5" />
                <span className="leading-relaxed">{errorMessage}</span>
              </div>
            )}

            {loginSuccess && (
              <div className="p-3.5 rounded-xl bg-emerald-950/40 border border-emerald-800/80 text-xs text-emerald-300 flex items-center gap-2 animate-fadeIn">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Authentication confirmed. Opening operations dashboard...</span>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              {/* Email field */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  Administrator Email
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@saintstech.co.ke"
                    className="w-full text-xs pl-10 pr-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-hidden focus:ring-2 focus:ring-sky-500 transition-all"
                  />
                </div>
              </div>

              {/* Password field */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-300">
                    Console Password
                  </label>
                  <button
                    type="button"
                    onClick={handleFillDemoCredentials}
                    className="text-[11px] text-sky-400 hover:text-sky-300 font-medium cursor-pointer"
                  >
                    Auto-Fill Demo
                  </button>
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter security password..."
                    className="w-full text-xs pl-10 pr-10 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-hidden focus:ring-2 focus:ring-sky-500 transition-all font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-1 text-slate-500 hover:text-slate-300 absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Remember me & Security info */}
              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-3.5 h-3.5 rounded text-sky-500 focus:ring-sky-500 bg-slate-950 border-slate-800 cursor-pointer"
                  />
                  <span className="text-xs text-slate-400">Keep session active</span>
                </label>

                <span className="text-[11px] text-slate-500">
                  Role: Super Admin
                </span>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting || loginSuccess}
                className="w-full py-3 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl shadow-lg shadow-sky-600/25 flex items-center justify-center gap-2 transition-all cursor-pointer mt-2"
              >
                {isSubmitting ? (
                  <span>Authenticating Admin...</span>
                ) : (
                  <>
                    <span>Sign In to Admin Console</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Quick Demo Access Box */}
            <div className="p-3.5 bg-slate-950/80 rounded-xl border border-slate-800/80 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                  <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                  <span>Admin Credentials:</span>
                </div>
                <button
                  type="button"
                  onClick={handleFillDemoCredentials}
                  className="text-[10px] font-bold px-2 py-0.5 rounded bg-sky-600/20 text-sky-300 border border-sky-500/30 hover:bg-sky-600/30 cursor-pointer"
                >
                  Quick Fill
                </button>
              </div>
              <div className="text-[11px] text-slate-400 font-mono space-y-0.5">
                <div>Email: <span className="text-slate-200">admin@saintstech.co.ke</span></div>
                <div>Password: <span className="text-slate-200">admin123</span></div>
              </div>
            </div>
          </div>

          {/* Footer note */}
          <div className="text-center space-y-1 text-xs text-slate-500">
            <div className="flex items-center justify-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-slate-400" />
              <span>Juja Road 478/B Green Mall, Nairobi, Kenya</span>
            </div>
            <p className="text-[11px] text-slate-400">
              Saints Tech Solutions • Authorized personnel only
            </p>
          </div>
        </div>
      </main>

      {/* Bottom Legal / Status Bar */}
      <footer className="px-6 py-4 border-t border-slate-900 bg-slate-950 text-center text-[11px] text-slate-400">
        &copy; {new Date().getFullYear()} Saints Tech Solutions. Protected under Kenyan Industrial Standards & Cyber Security Regulations.
      </footer>
    </div>
  );
};
